/**
 * DeploymentField - Model deployment selector for Agent nodes
 *
 * Fetches available deployments from the agent connection's resourceId
 * and displays them in a combobox that allows both selection and manual entry.
 *
 * For GitHub Copilot connections (no ARM resource), fetches available models
 * from the backend's `listModels` catalog instead.
 */

import { useMemo, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Combobox,
  Option,
  Field,
  Button,
  Spinner,
  Text,
} from '@coreai/fluentui-react';
import { ArrowSyncRegular } from '@fluentui/react-icons';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useDeploymentOptions, type DeploymentOption } from '@/hooks/useAzureResources';
import { useGhcpModels } from '@/hooks/useGhcpModels';
import { useManagedModels } from '@/hooks/useManagedModels';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { getAgentReferenceProvider } from '@/components/WorkflowDesigner/workflowHelpers';
import {
  MANAGED_MODEL_CONNECTION_PROVIDER_TYPE,
  MANAGED_MODEL_REFERENCE_KEY,
  MANAGED_MODEL_DISPLAY_NAME,
  toAccountArmResourceId,
} from '@/utils/connections';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalS,
  },
  combobox: {
    flex: 1,
    minWidth: 0,
  },
  refreshButton: {
    flexShrink: 0,
  },
  hint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  optionSubtext: {
    color: tokens.colorNeutralForeground3,
  },
  listbox: {
    maxHeight: '320px !important',
    overflowY: 'auto',
  },
});

export interface DeploymentFieldProps {
  /** Connection reference name to look up resourceId */
  connectionReferenceName: string | undefined;
  /** Currently selected deployment name */
  value: string;
  /** Callback when selection changes */
  onChange: (deployment: DeploymentOption) => void;
  /** Field label */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Validation error message */
  error?: string;
  /** Description/hint text */
  description?: string;
  /**
   * Whether the owning Agent action is a managed agent (GHCP harness). Used to
   * scope the GHCP model-listing behavior — see the `useGhcpModels` call below.
   * Workflow (native) agents are left on their original code path.
   */
  isCodingAgent?: boolean;
  /**
   * Whether to fetch the GHCP `/models` catalog. Gated by the caller on the
   * `githubCopilotConnection` flag. When false, GHCP connections keep the
   * freeform combobox and skip the fetch.
   */
  ghcpModelsEnabled?: boolean;
  /** Whether users may enter a deployment that is not in the fetched list. */
  allowCustomValue?: boolean;
  /** Direct resource ID for contexts that do not have a connection reference key. */
  resourceIdOverride?: string;
}

export const DeploymentField = ({
  connectionReferenceName,
  value,
  onChange,
  label = 'Model Deployment',
  required = false,
  disabled = false,
  error,
  description,
  isCodingAgent = false,
  ghcpModelsEnabled = false,
  allowCustomValue = true,
  resourceIdOverride,
}: DeploymentFieldProps) => {
  const styles = useStyles();

  // Get resourceId from connection references (connectionStore) first,
  // then fall back to workflow store's connections.json for persisted data.
  // This ensures newly created connections work immediately without waiting for save.
  const connectionReferences = useConnectionStore((state) => state.references);
  const workflowConnections = useWorkflowStore((state) => state.connections);
  const agentConnections = workflowConnections?.agentConnections;

  // Detect whether the connection is a GitHub Copilot connection
  const isGhcpConnection = useMemo(() => {
    if (!connectionReferenceName) return false;
    const reference = connectionReferences[connectionReferenceName];
    if (reference) {
      return getAgentReferenceProvider(reference) === 'GitHubCopilot';
    }
    return false;
  }, [connectionReferenceName, connectionReferences]);

  // Detect whether the connection is a Microsoft-managed connection.
  // These connections have no ARM resourceId — the deployment list is served
  // by the runtime's curated model catalog, with control plane as a compatibility fallback.
  // Match by canonical ref key OR by the trimmed `connectionProperties.type`
  // marker so existing Microsoft-managed workflows load correctly regardless of ref name.
  const isManagedModelConnection = useMemo(() => {
    if (!connectionReferenceName) return false;
    if (connectionReferenceName === MANAGED_MODEL_REFERENCE_KEY) return true;
    const reference = connectionReferences[connectionReferenceName];
    const props = reference?.connectionProperties as Record<string, unknown> | undefined;
    return (
      typeof props?.type === 'string' &&
      props.type.trim().toLowerCase() === MANAGED_MODEL_CONNECTION_PROVIDER_TYPE.toLowerCase()
    );
  }, [connectionReferenceName, connectionReferences]);

  const { projectName } = useCurrentProject();
  const {
    models: managedModels,
    isLoading: isLoadingManagedModels,
    error: managedModelsError,
    refetch: refetchManagedModels,
  } = useManagedModels(projectName, isManagedModelConnection);

  const resourceId = useMemo(() => {
    if (isGhcpConnection || isManagedModelConnection) return undefined;
    if (resourceIdOverride) {
      return toAccountArmResourceId(resourceIdOverride);
    }
    if (!connectionReferenceName) return undefined;

    // First check connectionStore references (includes newly created connections)
    const reference = connectionReferences[connectionReferenceName];
    if (reference) {
      // resourceId can be in connectionProperties or at root level
      const props = reference.connectionProperties as Record<string, unknown> | undefined;
      if (props?.resourceId && typeof props.resourceId === 'string') {
        return toAccountArmResourceId(props.resourceId);
      }
      // Also check if resourceId is stored directly on the reference (for legacy format)
      const refAny = reference as unknown as Record<string, unknown>;
      if (refAny.resourceId && typeof refAny.resourceId === 'string') {
        return toAccountArmResourceId(refAny.resourceId);
      }
    }

    // Fall back to workflow store's agentConnections (persisted data)
    if (agentConnections) {
      const connection = agentConnections[connectionReferenceName];
      if (connection?.resourceId) {
        return toAccountArmResourceId(connection.resourceId);
      }
    }

    return undefined;
  }, [
    connectionReferenceName,
    connectionReferences,
    agentConnections,
    isGhcpConnection,
    isManagedModelConnection,
    resourceIdOverride,
  ]);

  // --- ARM Deployments (AzureOpenAI / Foundry) ---
  const {
    deployments,
    isLoading: isLoadingDeployments,
    error: fetchError,
    refetch,
  } = useDeploymentOptions(resourceId ?? '', !!resourceId);

  // --- GitHub Copilot models (dynamic fetch through the data plane) ---
  // The GHCP `/models` listing path is gated behind the `githubCopilotConnection`
  // flag, surfaced via `ghcpModelsEnabled`. When enabled we fetch the model catalog
  // so the field becomes a real picker; when off we skip the request and let the user
  // type the model name into the freeform combobox.
  const {
    models: ghcpModels,
    isLoading: isLoadingGhcp,
    error: ghcpFetchError,
    refetch: refetchGhcp,
  } = useGhcpModels(
    connectionReferenceName ?? '',
    isGhcpConnection && ghcpModelsEnabled,
  );

  // Unified loading / error state
  const isLoading = isManagedModelConnection
    ? isLoadingManagedModels
    : isGhcpConnection
      ? isLoadingGhcp
      : isLoadingDeployments;
  const activeFetchError = isManagedModelConnection
    ? managedModelsError
    : isGhcpConnection
      ? ghcpFetchError
      : fetchError;

  // Build options from either source
  const options = useMemo(() => {
    if (isManagedModelConnection) {
      // Microsoft-managed models have no ARM deployment/version — surface the branded
      // "Microsoft-managed" label in the subtext position (parity with the
      // BYOM `modelName (modelVersion)` row below), and prepend the curated
      // catalog's `displayName` when it carries more information than the
      // bare model id.
      return managedModels.map((m) => ({
        key: m.id,
        text: m.id,
        subtext:
          m.displayName && m.displayName !== m.id
            ? `${m.displayName} · ${MANAGED_MODEL_DISPLAY_NAME}`
            : MANAGED_MODEL_DISPLAY_NAME,
      }));
    }
    if (isGhcpConnection) {
      return ghcpModels.map((model) => ({
        key: model.id,
        text: model.id,
        subtext: model.name !== model.id ? model.name : '',
      }));
    }
    return deployments.map((deployment) => ({
      key: deployment.name,
      text: deployment.name,
      subtext: `${deployment.modelName} (${deployment.modelVersion})`,
    }));
  }, [isManagedModelConnection, managedModels, isGhcpConnection, ghcpModels, deployments]);

  // Handle selection from dropdown
  const handleSelect = useCallback(
    (_event: unknown, data: { optionValue?: string | undefined }) => {
      const selected = data.optionValue;
      if (selected) {
        if (isManagedModelConnection) {
          // Microsoft-managed models have no ARM identity: emit the curated model id as
          // both `name` and `modelName`, with `modelFormat: 'MicrosoftManaged'`
          // so downstream serialization tags the deployment as Microsoft-managed.
          onChange({ id: '', name: selected, modelName: selected, modelFormat: 'MicrosoftManaged', modelVersion: '' });
        } else if (isGhcpConnection) {
          onChange({ id: '', name: selected, modelName: selected, modelFormat: 'GitHubCopilot', modelVersion: '' });
        } else {
          const deploymentOptionSelected = deployments.find((d) => d.name === selected);
          onChange(deploymentOptionSelected ?? { id: '', name: selected, modelName: '', modelFormat: '', modelVersion: '' });
        }
      }
    },
    [onChange, deployments, isGhcpConnection, isManagedModelConnection]
  );

  // Handle manual input in combobox
  const handleInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      if (isManagedModelConnection) {
        onChange({ id: '', name: inputValue, modelName: inputValue, modelFormat: 'MicrosoftManaged', modelVersion: '' });
      } else if (isGhcpConnection) {
        onChange({ id: '', name: inputValue, modelName: inputValue, modelFormat: 'GitHubCopilot', modelVersion: '' });
      } else {
        const deploymentOptionSelected = deployments.find((d) => d.name === inputValue);
        onChange(deploymentOptionSelected ?? { id: '', name: inputValue, modelName: '', modelFormat: '', modelVersion: '' });
      }
    },
    [onChange, deployments, isGhcpConnection, isManagedModelConnection]
  );

  const handleRefresh = useCallback(() => {
    if (isManagedModelConnection) {
      refetchManagedModels();
    } else if (isGhcpConnection) {
      refetchGhcp();
    } else {
      refetch();
    }
  }, [isManagedModelConnection, isGhcpConnection, refetch, refetchGhcp, refetchManagedModels]);

  // For GHCP/Microsoft-managed, field is enabled as long as connection exists; for ARM, requires resourceId
  const isDisabled = disabled || (!isGhcpConnection && !isManagedModelConnection && !resourceId);
  const validationState = error || activeFetchError ? 'error' : 'none';
  const validationMessage = error ?? activeFetchError?.message ?? null;

  // Determine placeholder text
  const getPlaceholder = () => {
    if (!connectionReferenceName && !resourceId) {
      return 'Configure connection first';
    }
    if (isManagedModelConnection) {
      if (isLoading) return 'Loading Microsoft-managed models...';
      if (managedModels.length === 0) return 'No Microsoft-managed models available in this region';
      return 'Select a Microsoft-managed model';
    }
    if (isGhcpConnection) {
      if (isLoading) return 'Loading models...';
      if (ghcpModels.length === 0) return 'Enter model name';
      return allowCustomValue ? 'Select or enter model name' : 'Select a model';
    }
    if (!resourceId) {
      return 'No resource ID in connection';
    }
    if (isLoading) {
      return 'Loading deployments...';
    }
    if (deployments.length === 0) {
      return allowCustomValue ? 'Enter deployment name' : 'No chat-completion deployments available';
    }
    return allowCustomValue
      ? 'Select or enter deployment name'
      : 'Select a deployment';
  };

  return (
    <div className={styles.container}>
      <Field
        label={label}
        required={required}
        validationState={validationState}
        validationMessage={validationMessage}
        {...(description ? { hint: description } : {})}
      >
        <div className={styles.fieldRow}>
          <Combobox
            className={styles.combobox}
            value={value}
            selectedOptions={value ? [value] : []}
            onOptionSelect={handleSelect}
            {...(allowCustomValue ? { onInput: handleInput } : {})}
            disabled={isDisabled || isLoading}
            placeholder={getPlaceholder()}
            aria-label={label}
            autoComplete="off"
            {...(isLoading ? { expandIcon: <Spinner size="tiny" /> } : {})}
            freeform={allowCustomValue}
            listbox={{
              className: styles.listbox,
            }}
          >
            {options.map((option) => (
              <Option key={option.key} value={option.key} text={option.text}>
                <div>
                  <Text weight="semibold">{option.text}</Text>
                  {option.subtext && (
                    <>
                      <br />
                      <Text size={200} className={styles.optionSubtext}>
                        {option.subtext}
                      </Text>
                    </>
                  )}
                </div>
              </Option>
            ))}
          </Combobox>
          {(isManagedModelConnection || isGhcpConnection || resourceId) && !isLoading && !(isCodingAgent && isGhcpConnection) && (
            <Button
              className={styles.refreshButton}
              appearance="subtle"
              icon={<ArrowSyncRegular />}
              onClick={handleRefresh}
              disabled={isLoading || isDisabled}
              aria-label={isManagedModelConnection ? 'Refresh Microsoft-managed models' : isGhcpConnection ? 'Refresh models' : 'Refresh deployments'}
              title={isManagedModelConnection ? 'Refresh Microsoft-managed models' : isGhcpConnection ? 'Refresh models' : 'Refresh deployments'}
            />
          )}
        </div>
      </Field>
      {!isManagedModelConnection && !isGhcpConnection && !resourceId && connectionReferenceName && (
        <Text className={styles.hint}>
          Connection does not have a resourceId. Enter the deployment name manually.
        </Text>
      )}
      <Text className={styles.hint} role="status">
        {resourceId && !isLoading && !activeFetchError && deployments.length === 0
          ? 'No ready chat-completion deployments found. Create a chat-completion deployment in Azure, then refresh.'
          : ''}
      </Text>
    </div>
  );
};

DeploymentField.displayName = 'DeploymentField';
